import _, {isEmpty, omit} from 'lodash'
import moment from 'moment'
import {bsMergeModelChangeMapAction, bsMergeResponseAction, invalidateCacheAction, refreshBSModelAction} from 'module/BSCollection/action/BSCollectionAction'
import {generateIndexScopedDates, getIndexScopedDate} from 'module/BSEntry/BSUtil'
import {BSCollectionDict} from 'module/BSCollection/dict/BSCollectionDict'
import {BSAttr} from 'module/BSEntry/BSAttr';
import {BSModelIDType, BSModelMeta, BSModelType, BSOwner, BSReducerType, BSSingleCollection} from 'module/BSCollection/type/BSCollectionStateType';
import {createReducer, isAnyOf} from 'util/redux';
import {SisNameBS} from 'module/BSCollection/type/SisNameBS';
import {log} from 'common/log';
import {Booking} from 'generated/JavaTOmodifications'
import {isBooking} from 'module/BSEntry/SisNameGuard'

interface BSParams<T extends BSModelType> {
	sisName: SisNameBS
	data: (T | null)[]
	owners: BSOwner[]
	fetchDates: number[]
	scopedDates: number[]
	timestamp: number
	startTS: number
	endTS: number
	maxCacheCount: number
}

function getBSParams<T extends BSModelType>(
	p: Omit<BSParams<T>,
		'scopedDates'
		| 'timestamp'
		| 'maxCacheCount'>,
): BSParams<T> {
	const dict = BSCollectionDict[p.sisName]
	return {
		...p,
		scopedDates: _.uniq(p.fetchDates.map(d => getIndexScopedDate(p.sisName, d))),
		timestamp: +moment(),
		maxCacheCount: dict.maxCacheCount,
	}
}

function setCacheTS<T extends BSModelType, ID extends BSModelIDType>(
	state: BSSingleCollection<T, ID>,
	{owners, scopedDates, timestamp}: Pick<BSParams<T>, 'owners' | 'scopedDates' | 'timestamp'>,
): BSSingleCollection<T, ID> {
	let odt = state.ownerDateFetchTimestamps
	if (scopedDates.length > 1000 || owners.length > 100) {
		log.warn(
			'BSCollection setCache got too many lines (performance issues!).',
			'owner count: ', owners.length,
			', date count: ', scopedDates.length,
		)
	}
	for (let owner of owners) {
		for (let date of scopedDates) {
			const dt = odt[owner] || {}
			if (!timestamp) {
				//remove if present
				if (dt[date]) {
					const {[date]: x, ...dtNew} = dt
					odt = {
						...odt,
						[owner]: dtNew,
					}
				}// else do nothing (deletion of non-existing timestamp)
			} else {
				//add
				odt = {
					...odt,
					[owner]: {
						...dt,
						[date]: timestamp,
					},
				}
			}
		}
	}
	return {
		...state,
		ownerDateFetchTimestamps: odt,
	}
}

function enrichMeta<T extends BSModelType>(model: T, timestamp: number): T & BSModelMeta {
	return {
		...model,
		_owner: (model as any)[BSCollectionDict[model.sisName].ownerAttr] || '' as BSOwner,
		_startTS: +BSAttr.getStartDateTime(model),
		_endTS: +BSAttr.getEndDateTime(model),
		_cacheTS: timestamp,
	}
}

function addModels<T extends BSModelType, ID extends BSModelIDType>(state: BSSingleCollection<T, ID>, models: (T | null | undefined)[], timestamp: number) {
	if (!models || !models.length) return state
	const changes: typeof state.byId = {}
	models.forEach(model => {
		if (!model) return
		const id = BSAttr.getId(model)
		if (id == null) return
		changes[id] = enrichMeta(model, timestamp)
	})
	if (isEmpty(changes)) return state
	return {
		...state,
		byId: {
			...state.byId,
			...changes,
		},
	}
}

function deleteModels<T extends BSModelType, ID extends BSModelIDType>(state: BSSingleCollection<T, ID>, modelOrIdList: (T | null | undefined | ID)[]): BSSingleCollection<T, ID> {
	const ids = modelOrIdList.flatMap(model => {
		if (model === null || model === undefined) return []
		let id: BSModelIDType;
		if (typeof model === 'string' || typeof model === 'number') {
			id = model
		} else {
			id = BSAttr.getId(model) as BSModelIDType;
		}
		if (id == null) return []
		else return [id]
	})
	if (!ids.length) return state;
	return {...state, byId: omit(state.byId, ...ids)}
}

function cleanById<T extends BSModelType, ID extends BSModelIDType>(
	state: BSSingleCollection<T, ID>,
	params: BSParams<T>,
): BSSingleCollection<T, ID> {
	const entries = Object.entries(state.byId)
	let cleanTS = -1
	const maxExceeded = _.size(state.byId) > params.maxCacheCount
	if (maxExceeded) {
		const cachedTS = _.sortedUniq(entries.map(([id, model]) => model._cacheTS).sort())
		const cachedTSLength = cachedTS.length
		if (cachedTSLength < 10) {
			// bail out. do not cache anything, because cache is too small.
			log.debug(params.sisName, 'reset cache, because overfull')
			return {
				...state,
				byId: {},
				ownerDateFetchTimestamps: {},
			}
		} else {
			// keep last 5
			cleanTS = cachedTS[cachedTSLength - 5]
			log.debug(params.sisName, 'cleaned until:', cleanTS)
			const odt = state.ownerDateFetchTimestamps
			const cleanedODT: typeof odt = {}
			for (let [owner, dt] of Object.entries(odt)) {
				const cleanedDT: typeof dt = {}
				for (let [date, ts] of Object.entries(dt)) {
					if (ts > cleanTS) {
						cleanedDT[date] = ts
					}
				}
				if (!isEmpty(cleanedDT)) cleanedODT[owner] = cleanedDT
			}
			state = {...state, ownerDateFetchTimestamps: cleanedODT}
		}
	}

	const cleanFromCache: ID[] = []
	for (let [id, model] of entries) {
		if (
			// clean because cache exceeded
			!model._cacheTS || cleanTS >= model._cacheTS ||
			params.owners.includes(model._owner) &&
			// clean same range
			(isBooking(model) ?
					params.fetchDates.includes((model as Booking).bookingDate) // booking call always only returns bookings on same day
					:
					(model._startTS <= params.endTS && model._endTS >= params.startTS)
			)
		) {
			cleanFromCache.push(id as ID)
		}
		state = deleteModels(state, cleanFromCache)
	}
	log.debug(params.sisName, 'delete models', cleanFromCache)
	return state
}

// Reducer
const SingleCollectionReducer = createReducer(
	{byId: {}, ownerDateFetchTimestamps: {}} as BSSingleCollection<BSModelType, BSModelIDType>, builder => builder
		.addCase(refreshBSModelAction, (state, {payload}) => {
			if (payload.remove) state = deleteModels(state, payload.remove)
			const timestamp = +moment()
			if (payload.add) state = addModels(state, payload.add, timestamp)
			return state
		})
		.addCase(bsMergeResponseAction, (state, action) => {
			const {data} = action.payload
			const params = getBSParams(action.payload)
			state = cleanById(state, params)
			state = addModels(state, data, params.timestamp)
			state = setCacheTS(state, params)
			return state
		})
		.addCase(bsMergeModelChangeMapAction, (state, action) => {
			const {modelMap} = action.payload;
			const timestamp = +moment()
			const toAdd = []
			const toDelete = []
			for (let [id, model] of Object.entries(modelMap)) {
				if (model) toAdd.push(model)
				else toDelete.push(id)
			}
			state = addModels(state, toAdd, timestamp)
			state = deleteModels(state, toDelete)
			return state
		}).addCase(invalidateCacheAction, (state, {payload}) => {
			const {sisName, owners, startTS, endTS} = payload
			console.log(sisName, moment(startTS).format('DD.MM.YYYY HH:mm:ss'), moment(endTS).format('DD.MM.YYYY HH:mm:ss'))
			state = setCacheTS(state, {
				owners: owners,
				scopedDates: generateIndexScopedDates(sisName, startTS, endTS),
				timestamp: 0,
			})
			return state
		}),
)


const BSCollectionReducer = createReducer({} as BSReducerType, builder => builder
	.addMatcher(
		isAnyOf(refreshBSModelAction, bsMergeResponseAction, bsMergeModelChangeMapAction, invalidateCacheAction),
		function (state, action) {
			const sisName = action.payload?.sisName as SisNameBS;
			if (sisName) {
				return {
					...state,
					[sisName]: SingleCollectionReducer(state[sisName], action),
				}
			}
			return state
		}),
)
export const BSCollectionReducerNS = {BSCollection: BSCollectionReducer}
export type BSCollectionStateType = { BSCollection: BSReducerType }